import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { isAnchorEqual } from 'utils/helpers';

/**
 * Event callback on route change when user presses the browser navigation icons
 * @param hash the browser hash value (exculding leading "#" char)
 */
export type Callback = (hash: string) => void;
/**
 * Apply hash string to the url based on given key and is required
 * @param hashKey key to use for the hash on current url
 * @param onChange Optional event handler when the location gets changed
 */
export default function useHash(hashKey: string, onChange: Callback = () => {}) {
  /**
   * retrieve the history hook object from 'react-router'
   */
  const history = useHistory();

  // Update the browser location url hash with new given hashKey
  useEffect(() => {
    // Don't push changes to history if the user has already navigated to this route, eg. when using browser back buttons
    if (!isAnchorEqual(history.location.hash, hashKey)) {
      history.push(`#${hashKey}`);
    }
  }, [hashKey, history]);

  // Listen to changes in the browser navigation buttons on the location "hash" property
  useEffect(() => {
    return history.listen(location => {
      // Remove the leading "#" char from the hashKey value
      const newHash = location.hash.length > 1 ? location.hash.substr(1) : '';
      // Avoid reloading the same step again if there was no change
      if (newHash && !isAnchorEqual(newHash, hashKey)) {
        onChange(newHash);
      }
    });
  }, [hashKey, history, onChange]);
}
